import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { eswiUtils } from "@/tra/tancis/eswi/comn";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_EXT_AUTH_INFO,
    SG_EXT_USR_AUTH_LIST,
} from "@/tra/tancis/ptli/usr/extauth/services/PtliExtAuthService";
import { PTLI0924003Q } from "@/tra/tancis/ptli/usr/extauth/PTLI0924003Q";

export const PTLI0924002S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0050", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { usrDmnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const param = useParams();

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        extAuthForm: useForm({
            defaultSchema: SF_EXT_AUTH_INFO,
        }),
    };

    /*
     * Declaration Grid
     * !== Grid 정의 ==!
     */
    const grid = {
        extAuthUsrList: useGrid({
            defaultSchema: SG_EXT_USR_AUTH_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getExtAuthUsrDtl: useFetch({
            api: (data) => APIS.getExtAuthUsrDtl(data),
            onSuccess: (data: any) => {
                const { content } = data.extAuthUsrInfo;
                form.extAuthForm.setValues(content);
            },
            onError: () => modal.openModal({ content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        saveExtUsrAuthList: useFetch({
            api: () =>
                APIS.saveExtUsrAuthList({
                    xtrnUsrUuid: param["xtrnUsrUuid"],
                    extAuthUsrList: grid.extAuthUsrList.getData(),
                }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00062" }); // 정상적으로 저장되었습니다.
                fetch.getExtAuthUsrDtl.fetch({ xtrnUsrUuid: param["xtrnUsrUuid"] });
            },
            onError: () => modal.openModal({ content: "msg.com.00026" }), // Failed to save. !==  저장이 실패하였습니다. ==!
        }),
    };
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */

    const handler = {
        saveExtUsrAuthList: () => {
            const list = grid.extAuthUsrList.getData({ excludes: ["origin"] });
            if (list.length === 0) {
                modal.openModal({ content: "msg.ptl.00741" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => fetch.saveExtUsrAuthList.fetch(),
            });
        },
        onBtnGrdAddRowClick: () => {
            const data = grid.extAuthUsrList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.ptl.00745" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            const modalCallback = (list: any) => {
                const authList = grid.extAuthUsrList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.extAuthUsrList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "md",
                content: (
                    <PTLI0924003Q
                        usrDmnId={param["usrDmnId"]}
                        extAuthUsrList={grid.extAuthUsrList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.extAuthUsrList.getChecked())) {
                modal.openModal({ content: "msg.com.00086" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.extAuthUsrList.deleteRow("checkbox");
                },
            });
        },
    };

    const render = {};

    useEffect(() => {
        fetch.getExtAuthUsrDtl.fetch({ xtrnUsrUuid: param["xtrnUsrUuid"] });
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.extAuthForm.schema.xtrnUsrIdntfNo} />
                            <Group.Control {...form.extAuthForm.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.extAuthForm.schema.usrMoblNo} />
                            <Group.Control {...form.extAuthForm.schema.usrEmlAddr} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.extAuthUsrList.grid} data={fetch.getExtAuthUsrDtl.data?.extAuthUsrList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button role={"list"} onClick={() => navigate(`${URLS.ptli0924001q}`)} />
                </Layout.Left>
                <Layout.Right>
                    <Button role={"save"} onClick={handler.saveExtUsrAuthList} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
